import { useMemo, useState } from 'react';
import useCustomSearchParams from '../useCustomSearchParams';
import { SortOrder } from 'components/molecule/table/RenderTable';
import { addDateFilterField, addFilterField, isFunction } from 'utils/helper/helper';
import { PaginationParam } from 'constants/params/paramKeys';
import { buildSortClause, buildSortParamsForUpdate, getDefaultTimeColumn, withSortInFilters } from 'components/molecule/SortingUtils/SortingUtils';
import { usePersistParams } from '../usePersistParams/usePersistParams';
import { formToParams } from 'utils/helper/filterBarHelpers';
import { EMPTY, omitField } from 'utils/helper/object';
import { CommonFilterFields } from 'constants/filters/commonFilterFields';

const stateFilter = (state) => omitField(state, 'currentPage');
const PER_PAGE_DEFAULT = 20;
const NO_SORT = { order: SortOrder.NONE };

export const useParamsState = ({ filterFields = EMPTY, filters, perPage = PER_PAGE_DEFAULT, defaultPage = 1, defaultSort, timeColumn, defaultValues, include, persist } = {}) => {
  const [fields] = useState(() => ({ ...filterFields, ...(timeColumn ? CommonFilterFields : {}) }));
  const { initial, restoring } = usePersistParams('query', { filter: stateFilter, enable: persist });
  const { filterFormValues, paramsObject, isLoadingParams, updateSearchParams, deleteSearchParams, filterFormKv } = useCustomSearchParams(fields, { initial });
  const allFields = useMemo(() => Object.values(fields).map((f) => f.key), [fields]);
  const values = useMemo(() => (filterFormValues ? { ...defaultValues, ...filterFormValues } : null), [defaultValues, filterFormValues]);
  const currenTimeColumn = timeColumn ? getDefaultTimeColumn(paramsObject, timeColumn) : null;
  const params = paramsObject || {};
  const setPagination = (pagination) => {
    const next = isFunction(pagination) ? pagination() : pagination;

    updateSearchParams({
      ...addFilterField(PaginationParam.CURRENT_PAGE, next.currentPage),
      ...addFilterField(PaginationParam.PER_PAGE, next.perPage),
    });
  };

  const defaultSortOrder = useMemo(() => {
    if (isFunction(defaultSort)) {
      return paramsObject ? defaultSort(paramsObject) : NO_SORT;
    }

    return defaultSort || NO_SORT;
  }, [paramsObject, defaultSort]);

  const pagination = useMemo(
    () => ({
      perPage: parseInt(params.perPage ?? perPage),
      currentPage: parseInt(params.currentPage ?? defaultPage),
    }),
    [params.currentPage, params.perPage, perPage, defaultPage]
  );

  const sort = useMemo(() => {
    const column = params.sortColumn ?? defaultSortOrder?.column;
    if (column) {
      return {
        column,
        order: params.sortOrder ?? (defaultSortOrder?.order || SortOrder.DESC),
      };
    }
  }, [params.sortColumn, params.sortOrder, defaultSortOrder]);

  const setSort = (next) => {
    const newSort = buildSortParamsForUpdate(next, defaultSortOrder.column);
    updateSearchParams(newSort, { remove: [PaginationParam.CURRENT_PAGE] });
  };

  const sortClause = useMemo(() => (sort ? buildSortClause(sort, defaultSortOrder?.column) : null), [sort, defaultSortOrder]);

  const filterParams = useMemo(() => {
    if (!values) return EMPTY;

    let result = currenTimeColumn ? {
      ...addDateFilterField(`filter[${currenTimeColumn}_gteq]`, values.startDate),
      ...addDateFilterField(`filter[${currenTimeColumn}_lteq]`, values.endDate),
    } : {};

    if (filters) {
      result = { ...result, ...filters({ values, timeColumn: currenTimeColumn, params: paramsObject }) };
    }

    return withSortInFilters(result, sortClause);
  }, [values, sortClause, filters, currenTimeColumn, paramsObject]);

  function setFilter(data = {}, filterFields = fields) {
    updateSearchParams(
      {
        ...formToParams(data, filterFields),
      },
      { remove: [PaginationParam.CURRENT_PAGE] }
    );
  }

  function removeFilter(keys = allFields) {
    deleteSearchParams(keys.concat(PaginationParam.CURRENT_PAGE));
  }

  const queryParams = useMemo(
    () => ({
      page: pagination.currentPage,
      per_page: pagination.perPage,
      include,
      ...filterParams,
    }),
    [filterParams, pagination.currentPage, pagination.perPage, include]
  );

  return {
    queryParams,
    paramsObject,
    sort,
    setSort,
    sortClause,
    setFilter,
    removeFilter,
    pagination,
    setPagination,
    timeColumn: currenTimeColumn,
    isLoadingParams: isLoadingParams || restoring,
    restoring,
    values,
    filterFields: fields,
    filterFormKv,
  };
};
